/**
 * Field Type Detection Patterns
 * 
 * This module contains the patterns and rules for identifying different field types
 * based on data-name attributes and fallback detection methods.
 */

/**
 * Enumeration of supported field types for detection
 */
export enum FieldType {
  PHONE = 'phone',
  ZIP = 'zip', 
  DATE_OF_BIRTH = 'date_of_birth',
  CURRENCY = 'currency',
  SSN = 'ssn',
  UNKNOWN = 'unknown'
}

/**
 * Field detection patterns for data-name attribute matching
 */
export class FieldDetectionPatterns {
  /**
   * Primary patterns that match exact data-name values
   */
  private static readonly EXACT_PATTERNS: Record<FieldType, string[]> = {
    [FieldType.PHONE]: [
      'phone',
      'mobile', 
      'cell',
      'phone_number',
      'mobile_phone',
      'cell_phone',
      'telephone',
      'tel'
    ],
    [FieldType.ZIP]: [
      'zip',
      'zipcode',
      'zip_code',
      'address_zip',
      'postal_code',
      'postal'
    ],
    [FieldType.DATE_OF_BIRTH]: [
      'dob',
      'birth_date',
      'date_of_birth',
      'birthdate',
      'birth',
      'date_birth'
    ],
    [FieldType.CURRENCY]: [
      'income',
      'salary',
      'amount',
      'housing_payment',
      'rent',
      'mortgage',
      'payment',
      'monthly_income',
      'annual_income',
      'gross_income',
      'net_income',
      'debt_amount',
      'loan_amount',
      'balance'
    ],
    [FieldType.SSN]: [
      'ssn',
      'social_security',
      'social_security_number',
      'tax_id',
      'taxpayer_id'
    ],
    [FieldType.UNKNOWN]: []
  };

  /**
   * Secondary patterns using regex for partial matching (fallback detection)
   */
  private static readonly REGEX_PATTERNS: Record<FieldType, RegExp> = {
    [FieldType.PHONE]: /phone|mobile|cell|tel|telephone/i,
    [FieldType.ZIP]: /zip|postal/i,
    [FieldType.DATE_OF_BIRTH]: /birth|dob|date.*birth/i,
    [FieldType.CURRENCY]: /income|salary|amount|payment|debt|loan|balance|rent|mortgage/i,
    [FieldType.SSN]: /ssn|social.*security|tax.*id/i,
    [FieldType.UNKNOWN]: /^$/
  };

  /**
   * Input type patterns for type inference (tertiary detection)
   */
  private static readonly INPUT_TYPE_PATTERNS: Record<string, FieldType> = {
    'tel': FieldType.PHONE,
    'email': FieldType.UNKNOWN, // Not handled by this system
    'date': FieldType.DATE_OF_BIRTH,
    'number': FieldType.CURRENCY // Default assumption
  };

  /**
   * Placeholder text patterns for additional context
   */
  private static readonly PLACEHOLDER_PATTERNS: Record<FieldType, RegExp> = {
    [FieldType.PHONE]: /\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}|phone|mobile/i,
    [FieldType.ZIP]: /\d{5}(-\d{4})?|zip|postal/i,
    [FieldType.DATE_OF_BIRTH]: /mm\/dd\/yyyy|birth|dob/i,
    [FieldType.CURRENCY]: /\$|income|salary|amount/i,
    [FieldType.SSN]: /\d{3}-\d{2}-\d{4}|ssn|social/i,
    [FieldType.UNKNOWN]: /^$/
  };

  /**
   * Determine field type using exact data-name matching (primary detection)
   * @param dataName The data-name attribute value
   * @returns Field type or UNKNOWN if no match
   */
  public static determineFieldTypeExact(dataName: string): FieldType {
    if (!dataName) return FieldType.UNKNOWN;

    const normalizedName = dataName.toLowerCase().trim();
    
    for (const [fieldType, patterns] of Object.entries(this.EXACT_PATTERNS)) {
      if (patterns.includes(normalizedName)) {
        return fieldType as FieldType;
      }
    }

    return FieldType.UNKNOWN;
  }

  /**
   * Determine field type using regex patterns (secondary detection)
   * @param dataName The data-name attribute value
   * @returns Field type or UNKNOWN if no match
   */
  public static determineFieldTypeRegex(dataName: string): FieldType {
    if (!dataName) return FieldType.UNKNOWN;

    for (const [fieldType, pattern] of Object.entries(this.REGEX_PATTERNS)) {
      if (fieldType !== FieldType.UNKNOWN && pattern.test(dataName)) {
        return fieldType as FieldType;
      }
    }

    return FieldType.UNKNOWN;
  }

  /**
   * Determine field type from input type attribute (tertiary detection)
   * @param inputType The input type attribute value
   * @returns Field type or UNKNOWN if no match
   */
  public static determineFieldTypeFromInputType(inputType: string): FieldType {
    if (!inputType) return FieldType.UNKNOWN;
    
    return this.INPUT_TYPE_PATTERNS[inputType.toLowerCase()] || FieldType.UNKNOWN;
  }

  /**
   * Determine field type from placeholder text (additional context)
   * @param placeholder The placeholder attribute value
   * @returns Field type or UNKNOWN if no match
   */
  public static determineFieldTypeFromPlaceholder(placeholder: string): FieldType {
    if (!placeholder) return FieldType.UNKNOWN;

    for (const [fieldType, pattern] of Object.entries(this.PLACEHOLDER_PATTERNS)) {
      if (fieldType !== FieldType.UNKNOWN && pattern.test(placeholder)) {
        return fieldType as FieldType;
      }
    }

    return FieldType.UNKNOWN;
  }

  /**
   * Comprehensive field type determination using all available methods
   * @param dataName The data-name attribute value
   * @param inputType The input type attribute value
   * @param placeholder The placeholder attribute value
   * @param fieldName The name/id attribute value (fallback)
   * @returns Field type with confidence score
   */
  public static determineFieldType(
    dataName?: string,
    inputType?: string,
    placeholder?: string,
    fieldName?: string
  ): { fieldType: FieldType; confidence: number; method: string } {
    
    // Primary: Exact data-name matching (highest confidence)
    if (dataName) {
      const exactMatch = this.determineFieldTypeExact(dataName);
      if (exactMatch !== FieldType.UNKNOWN) {
        return { fieldType: exactMatch, confidence: 1.0, method: 'exact_data_name' };
      }

      // Secondary: Regex data-name matching (high confidence)
      const regexMatch = this.determineFieldTypeRegex(dataName);
      if (regexMatch !== FieldType.UNKNOWN) {
        return { fieldType: regexMatch, confidence: 0.8, method: 'regex_data_name' };
      }
    }

    // Fallback to field name/id if data-name not available
    if (fieldName && !dataName) {
      const regexMatch = this.determineFieldTypeRegex(fieldName);
      if (regexMatch !== FieldType.UNKNOWN) {
        return { fieldType: regexMatch, confidence: 0.6, method: 'regex_field_name' };
      }
    }

    // Tertiary: Input type matching (medium confidence)
    if (inputType) {
      const inputTypeMatch = this.determineFieldTypeFromInputType(inputType);
      if (inputTypeMatch !== FieldType.UNKNOWN) {
        return { fieldType: inputTypeMatch, confidence: 0.5, method: 'input_type' };
      }
    }

    // Additional context: Placeholder matching (lower confidence)
    if (placeholder) {
      const placeholderMatch = this.determineFieldTypeFromPlaceholder(placeholder);
      if (placeholderMatch !== FieldType.UNKNOWN) {
        return { fieldType: placeholderMatch, confidence: 0.3, method: 'placeholder' };
      }
    }

    return { fieldType: FieldType.UNKNOWN, confidence: 0.0, method: 'none' };
  }

  /**
   * Get all supported field types
   * @returns Array of supported field types
   */
  public static getSupportedFieldTypes(): FieldType[] {
    return [
      FieldType.PHONE,
      FieldType.ZIP,
      FieldType.DATE_OF_BIRTH,
      FieldType.CURRENCY,
      FieldType.SSN
    ];
  }

  /**
   * Check if a field type requires formatting
   * @param fieldType The field type to check
   * @returns Boolean indicating if formatting is required
   */
  public static requiresFormatting(fieldType: FieldType): boolean {
    return fieldType !== FieldType.UNKNOWN;
  }

  /**
   * Get expected format pattern for field type (for validation)
   * @param fieldType The field type
   * @returns Expected format pattern or null
   */
  public static getExpectedFormatPattern(fieldType: FieldType): string | null {
    const patterns = {
      [FieldType.PHONE]: 'XXX-XXX-XXXX',
      [FieldType.ZIP]: 'XXXXX',
      [FieldType.DATE_OF_BIRTH]: 'MM/DD/YYYY',
      [FieldType.CURRENCY]: '$X,XXX.XX',
      [FieldType.SSN]: 'XXX-XX-XXXX',
      [FieldType.UNKNOWN]: null
    };

    return patterns[fieldType] || null;
  }
}

/**
 * Field detection confidence levels
 */
export enum DetectionConfidence {
  HIGH = 1.0,      // Exact data-name match
  MEDIUM_HIGH = 0.8, // Regex data-name match  
  MEDIUM = 0.6,    // Field name fallback
  MEDIUM_LOW = 0.5, // Input type inference
  LOW = 0.3,       // Placeholder inference
  NONE = 0.0       // No detection possible
}

/**
 * Interface for field detection result
 */
export interface FieldDetectionResult {
  fieldType: FieldType;
  confidence: DetectionConfidence | number;
  method: string;
  requiresFormatting: boolean;
  expectedFormat?: string;
}